import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Brain, MessageSquare, TrendingUp, Bell, Zap, Crown } from 'lucide-react';
import { apiClient } from '../lib/api';

interface AiSettings {
  ai_assistant_enabled: boolean;
  ai_assistant_plan: string;
  ai_features: {
    natural_language: boolean;
    intelligent_responses: boolean;
    proactive_suggestions: boolean;
    wait_time_predictions: boolean;
  };
}

interface AiAnalytics {
  predictions?: {
    service_time: {
      average: number;
      p80: number;
      confidence: string;
    };
    confidence: string;
    sample_size: number;
  };
  suggestions?: Array<{
    type: string;
    priority: string;
    title: string;
    description: string;
    impact: string;
  }>;
  error?: string;
}

interface AiSettingsPanelProps {
  tenantId: string;
  isVisible: boolean;
  onClose: () => void;
  isSuperAdmin: boolean;
}

const AiSettingsPanel: React.FC<AiSettingsPanelProps> = ({
  tenantId,
  isVisible,
  onClose,
  isSuperAdmin
}) => {
  const [settings, setSettings] = useState<AiSettings>({
    ai_assistant_enabled: false,
    ai_assistant_plan: 'basic',
    ai_features: {
      natural_language: false,
      intelligent_responses: false,
      proactive_suggestions: false,
      wait_time_predictions: false
    }
  });
  const [analytics, setAnalytics] = useState<AiAnalytics>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    if (isVisible && tenantId) {
      loadSettings();
      if (settings.ai_assistant_enabled) {
        loadAnalytics();
      }
    }
  }, [isVisible, tenantId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getTenantSettings(tenantId);
      setSettings({
        ai_assistant_enabled: response.ai_assistant_enabled || false,
        ai_assistant_plan: response.ai_assistant_plan || 'basic',
        ai_features: response.ai_features || {
          natural_language: false,
          intelligent_responses: false,
          proactive_suggestions: false,
          wait_time_predictions: false
        }
      });
    } catch (error) {
      console.error('Failed to load AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await fetch(`/admin/tenant/${tenantId}/ai/analytics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to load AI analytics:', error);
      setAnalytics({ error: 'Failed to load analytics' });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await apiClient.updateTenantSettings(tenantId, settings);
      
      // Reload analytics if AI was just enabled
      if (settings.ai_assistant_enabled) {
        loadAnalytics();
      }
    } catch (error) {
      console.error('Failed to save AI settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateFeature = (feature: keyof AiSettings['ai_features'], enabled: boolean) => {
    setSettings(prev => ({
      ...prev,
      ai_features: {
        ...prev.ai_features,
        [feature]: enabled
      }
    }));
  };

  const updatePlan = (plan: string) => {
    setSettings(prev => ({
      ...prev,
      ai_assistant_plan: plan,
      ai_features: {
        // Reset features based on plan
        natural_language: plan !== 'basic',
        intelligent_responses: plan !== 'basic',
        proactive_suggestions: plan === 'premium' || plan === 'enterprise',
        wait_time_predictions: plan === 'premium' || plan === 'enterprise'
      }
    }));
  };

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'premium': return 'bg-blue-500';
      case 'enterprise': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'text-green-600';
      case 'moderate': return 'text-yellow-600';
      default: return 'text-red-600';
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Brain className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-semibold">AI Assistant Settings</h2>
              <Badge className={getPlanBadgeColor(settings.ai_assistant_plan)}>
                {settings.ai_assistant_plan.toUpperCase()}
              </Badge>
            </div>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <Tabs defaultValue="settings" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="settings">Settings</TabsTrigger>
                <TabsTrigger value="analytics" disabled={!settings.ai_assistant_enabled}>
                  Analytics
                </TabsTrigger>
                <TabsTrigger value="pricing">Pricing</TabsTrigger>
              </TabsList>

              <TabsContent value="settings" className="space-y-6">
                {!isSuperAdmin && (
                  <Alert>
                    <AlertDescription>
                      AI settings can only be modified by super administrators.
                    </AlertDescription>
                  </Alert>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Zap className="h-5 w-5" />
                      <span>AI Assistant</span>
                    </CardTitle>
                    <CardDescription>
                      Enable intelligent SMS responses and proactive notifications
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Enable AI Assistant</h4>
                        <p className="text-sm text-gray-600">
                          Activate AI-powered SMS responses for this tenant
                        </p>
                      </div>
                      <Switch
                        checked={settings.ai_assistant_enabled}
                        onCheckedChange={(checked) => 
                          setSettings(prev => ({ ...prev, ai_assistant_enabled: checked }))
                        }
                        disabled={!isSuperAdmin}
                      />
                    </div>

                    {settings.ai_assistant_enabled && (
                      <div className="space-y-4 pt-4 border-t">
                        <div>
                          <h4 className="font-medium mb-2">AI Plan</h4>
                          <div className="grid grid-cols-3 gap-2">
                            {['basic', 'premium', 'enterprise'].map((plan) => (
                              <Button
                                key={plan}
                                variant={settings.ai_assistant_plan === plan ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => updatePlan(plan)}
                                disabled={!isSuperAdmin}
                                className="capitalize"
                              >
                                {plan === 'premium' && <Crown className="h-4 w-4 mr-1" />}
                                {plan}
                              </Button>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-3">
                          <h4 className="font-medium">AI Features</h4>
                          
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <MessageSquare className="h-4 w-4 text-blue-500" />
                                <div>
                                  <p className="font-medium">Natural Language Processing</p>
                                  <p className="text-sm text-gray-600">
                                    Understand customer intent and respond naturally
                                  </p>
                                </div>
                              </div>
                              <Switch
                                checked={settings.ai_features.natural_language}
                                onCheckedChange={(checked) => updateFeature('natural_language', checked)}
                                disabled={!isSuperAdmin || settings.ai_assistant_plan === 'basic'}
                              />
                            </div>

                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <Brain className="h-4 w-4 text-purple-500" />
                                <div>
                                  <p className="font-medium">Intelligent Responses</p>
                                  <p className="text-sm text-gray-600">
                                    Context-aware replies based on queue status
                                  </p>
                                </div>
                              </div>
                              <Switch
                                checked={settings.ai_features.intelligent_responses}
                                onCheckedChange={(checked) => updateFeature('intelligent_responses', checked)}
                                disabled={!isSuperAdmin || settings.ai_assistant_plan === 'basic'}
                              />
                            </div>

                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <Bell className="h-4 w-4 text-green-500" />
                                <div>
                                  <p className="font-medium">Proactive Suggestions</p>
                                  <p className="text-sm text-gray-600">
                                    Send helpful notifications and suggestions
                                  </p>
                                </div>
                              </div>
                              <Switch
                                checked={settings.ai_features.proactive_suggestions}
                                onCheckedChange={(checked) => updateFeature('proactive_suggestions', checked)}
                                disabled={!isSuperAdmin || !['premium', 'enterprise'].includes(settings.ai_assistant_plan)}
                              />
                            </div>

                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <TrendingUp className="h-4 w-4 text-orange-500" />
                                <div>
                                  <p className="font-medium">Wait Time Predictions</p>
                                  <p className="text-sm text-gray-600">
                                    AI-powered accurate wait time estimates
                                  </p>
                                </div>
                              </div>
                              <Switch
                                checked={settings.ai_features.wait_time_predictions}
                                onCheckedChange={(checked) => updateFeature('wait_time_predictions', checked)}
                                disabled={!isSuperAdmin || !['premium', 'enterprise'].includes(settings.ai_assistant_plan)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {isSuperAdmin && (
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button onClick={saveSettings} disabled={saving}>
                      {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                      Save Settings
                    </Button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="analytics" className="space-y-6">
                {analyticsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : analytics.error ? (
                  <Alert>
                    <AlertDescription>{analytics.error}</AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-6">
                    {analytics.predictions && (
                      <Card>
                        <CardHeader>
                          <CardTitle>AI Predictions</CardTitle>
                          <CardDescription>
                            Machine learning insights from queue data
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-600">Average Service Time</p>
                              <p className="text-2xl font-bold">
                                {Math.round(analytics.predictions.service_time.average / 60)} min
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">P80 Service Time</p>
                              <p className="text-2xl font-bold">
                                {Math.round(analytics.predictions.service_time.p80 / 60)} min
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Prediction Confidence</p>
                              <p className={`text-lg font-semibold capitalize ${getConfidenceColor(analytics.predictions.confidence)}`}>
                                {analytics.predictions.confidence}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Sample Size</p>
                              <p className="text-lg font-semibold">
                                {analytics.predictions.sample_size} services
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {analytics.suggestions && analytics.suggestions.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>AI Recommendations</CardTitle>
                          <CardDescription>
                            Optimization suggestions based on queue patterns
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {analytics.suggestions.map((suggestion, index) => (
                              <div key={index} className="border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-medium">{suggestion.title}</h4>
                                  <Badge variant={suggestion.priority === 'high' ? 'destructive' : 
                                               suggestion.priority === 'medium' ? 'default' : 'secondary'}>
                                    {suggestion.priority} priority
                                  </Badge>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">{suggestion.description}</p>
                                <p className="text-sm font-medium text-green-600">{suggestion.impact}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <div className="flex justify-end">
                      <Button onClick={loadAnalytics} variant="outline">
                        Refresh Analytics
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="pricing" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="relative">
                    <CardHeader>
                      <CardTitle>Basic</CardTitle>
                      <CardDescription>Standard SMS responses</CardDescription>
                      <div className="text-2xl font-bold">Included</div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-sm">
                        <li>✓ Basic SMS commands</li>
                        <li>✓ Queue status updates</li>
                        <li>✓ Simple responses</li>
                        <li>✗ Natural language</li>
                        <li>✗ Proactive notifications</li>
                        <li>✗ AI predictions</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="relative border-blue-500 border-2">
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-blue-500">Recommended</Badge>
                    </div>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Crown className="h-5 w-5 mr-2 text-blue-500" />
                        Premium
                      </CardTitle>
                      <CardDescription>AI-powered intelligent responses</CardDescription>
                      <div className="text-2xl font-bold">$89/month</div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-sm">
                        <li>✓ Everything in Basic</li>
                        <li>✓ Natural language processing</li>
                        <li>✓ Intelligent responses</li>
                        <li>✓ Proactive notifications</li>
                        <li>✓ Wait time predictions</li>
                        <li>✓ Queue optimization insights</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Enterprise</CardTitle>
                      <CardDescription>Advanced AI with custom features</CardDescription>
                      <div className="text-2xl font-bold">$149/month</div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-sm">
                        <li>✓ Everything in Premium</li>
                        <li>✓ Custom AI training</li>
                        <li>✓ Advanced analytics</li>
                        <li>✓ Priority support</li>
                        <li>✓ Custom integrations</li>
                        <li>✓ Dedicated success manager</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiSettingsPanel;